import cn from 'classnames';
import type { ButtonHTMLAttributes } from 'react';
import { useContext, useMemo } from 'react';

import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { Loader } from '@webapp/common/components/loader';
import { CssUiComponent } from '@webapp/common/components/survey-custom';

import css from './button.css';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    color?: 'blue' | 'white' | 'transparent';
    fluid?: boolean;
    bordered?: boolean;
    loading?: boolean;
    className?: string;
    darkText?: boolean;
    title?: string;
    disabled?: boolean;
    slim?: boolean;
    responsive?: boolean;
    label?: string;
    borderColor?: 'blue';
    size?: 'small' | 'medium' | 'big';
}

export const Button: FC<ButtonProps> = ({
    bordered,
    children,
    className,
    color,
    darkText,
    disabled,
    fluid,
    label,
    loading,
    slim,
    responsive,
    type = 'button',
    borderColor,
    ...props
}) => {
    const {
        button: {
            root: {
                backgroundColor: backgroundColorFromSurvey = '',
                background: backgroundFromSurvey = '',
                color: buttonColor = '',
                width = '',
                height = '',
                borderRadius = '',
            } = {},
        },
    } = useContext(CustomStylesCtx);

    const style = useMemo(() => {
        let backgroundColor: string;
        let background: string;

        if (fluid && !bordered) {
            if (backgroundFromSurvey) {
                background = backgroundFromSurvey as string;
                backgroundColor = 'transparent';
            } else {
                backgroundColor = backgroundColorFromSurvey;
            }
        }

        return {
            background,
            backgroundColor,
            color: fluid && bordered ? buttonColor : undefined,
            width: width ? `${width}px` : undefined,
            height: height ? `${height}px` : undefined,
            borderRadius: borderRadius ? `${borderRadius}px` : undefined,
        };
    }, [backgroundFromSurvey, backgroundColorFromSurvey, bordered, buttonColor, fluid, width, height, borderRadius]);

    return (
        <button
            disabled={loading || disabled}
            style={style}
            type={type}
            className={cn(
                CssUiComponent.BUTTON,
                css.button,
                {
                    [css[color]]: color,
                    [css.slim]: slim,
                    [css.fluid]: fluid,
                    [css.disabled]: disabled,
                    [css.bordered]: bordered,
                    [css.loading]: loading,
                    [css.darkText]: darkText,
                    [css.responsive]: responsive,
                    [css[`borderColor_${borderColor}`]]: borderColor,
                },
                className,
            )}
            {...props}
        >
            <Loader
                className={cn(CssUiComponent.BUTTON_LOADER, css.loader)}
                inline={true}
                inlineElement={true}
                visible={loading}
            />
            <span className={cn(CssUiComponent.BUTTON_TEXT, css.contents)}>{label || children}</span>
        </button>
    );
};
